import { createSlice, createAsyncThunk, createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import * as partitionValuesApi from '@/api/modules/partitionValues';
import { normalizeData } from '@/utils/jsonapi';

/**
 * Entity adapter for partition values
 * Stores all partition values normalized by ID with partition_type relationship
 */
const partitionValuesAdapter = createEntityAdapter({
  selectId: (partitionValue) => partitionValue.id,
  sortComparer: (a, b) => a.sort_order - b.sort_order,
});

/**
 * Async thunk to fetch partition values for a partition type
 * Normalizes JSON:API response before storing in Redux
 *
 * @param {Object} params - Parameters object
 * @param {string} params.partitionTypeId - Partition type UUID
 * @param {Object} [params.queryOptions] - JSON:API query options
 * @param {Object} [params.queryOptions.filters] - Filter criteria (e.g., { value: { icontains: 'am' } })
 * @param {Array<string>|string} [params.queryOptions.sort] - Sort fields (e.g., ['sort_order'])
 * @param {Array<string>|string} [params.queryOptions.include] - Relations to include (e.g., ['partition_type'])
 * @param {number} [params.queryOptions.page] - Page number
 * @param {number} [params.queryOptions.pageSize] - Items per page
 */
export const fetchPartitionValuesThunk = createAsyncThunk(
  'partitionValues/fetchAll',
  async ({ partitionTypeId, queryOptions = {} }, { rejectWithValue }) => {
    try {
      const response = await partitionValuesApi.fetchPartitionValues(partitionTypeId, queryOptions);

      // Response is JSON:API format: { data: [...], meta: {...}, included: [...] }
      const values = normalizeData(response.data) || [];

      return { partitionTypeId, values, meta: response.meta, included: response.included };
    } catch (error) {
      let errorMessage = 'Failed to fetch partition values';
      if (error.response?.data?.errors?.[0]?.detail) {
        errorMessage = error.response.data.errors[0].detail;
      } else if (error.apiError?.message) {
        errorMessage = error.apiError.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Async thunk to create a partition value
 * Normalizes JSON:API response before storing in Redux
 */
export const createPartitionValueThunk = createAsyncThunk(
  'partitionValues/create',
  async ({ partitionTypeId, data }, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await partitionValuesApi.createPartitionValue(partitionTypeId, data);
      // Normalize single resource
      const value = normalizeData(jsonApiResponse.data);
      return { partitionTypeId, value };
    } catch (error) {
      let errorMessage = 'Failed to create partition value';
      if (error.response?.data?.errors?.[0]?.detail) {
        errorMessage = error.response.data.errors[0].detail;
      } else if (error.apiError?.message) {
        errorMessage = error.apiError.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Async thunk to update a partition value
 * Normalizes JSON:API response before storing in Redux
 */
export const updatePartitionValueThunk = createAsyncThunk(
  'partitionValues/update',
  async ({ partitionTypeId, id, data }, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await partitionValuesApi.updatePartitionValue(partitionTypeId, id, data);
      // Normalize single resource
      const value = normalizeData(jsonApiResponse.data);
      return { partitionTypeId, value };
    } catch (error) {
      let errorMessage = 'Failed to update partition value';
      if (error.response?.data?.errors?.[0]?.detail) {
        errorMessage = error.response.data.errors[0].detail;
      } else if (error.apiError?.message) {
        errorMessage = error.apiError.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Async thunk to delete a partition value
 */
export const deletePartitionValueThunk = createAsyncThunk(
  'partitionValues/delete',
  async ({ partitionTypeId, id }, { rejectWithValue }) => {
    try {
      await partitionValuesApi.deletePartitionValue(partitionTypeId, id);
      return { partitionTypeId, id };
    } catch (error) {
      let errorMessage = 'Failed to delete partition value';
      if (error.response?.data?.errors?.[0]?.detail) {
        errorMessage = error.response.data.errors[0].detail;
      } else if (error.apiError?.message) {
        errorMessage = error.apiError.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Async thunk to bulk create partition values
 * Normalizes JSON:API response before storing in Redux
 */
export const bulkCreatePartitionValuesThunk = createAsyncThunk(
  'partitionValues/bulkCreate',
  async ({ partitionTypeId, values }, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await partitionValuesApi.bulkCreatePartitionValues(partitionTypeId, values);
      // Normalize array of resources
      const created = normalizeData(jsonApiResponse.data) || [];
      return { partitionTypeId, values: created };
    } catch (error) {
      let errorMessage = 'Failed to bulk create partition values';
      if (error.response?.data?.errors?.[0]?.detail) {
        errorMessage = error.response.data.errors[0].detail;
      } else if (error.apiError?.message) {
        errorMessage = error.apiError.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Async thunk to reorder partition values
 * Normalizes JSON:API response before storing in Redux
 */
export const reorderPartitionValuesThunk = createAsyncThunk(
  'partitionValues/reorder',
  async ({ partitionTypeId, orderedIds }, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await partitionValuesApi.reorderPartitionValues(partitionTypeId, orderedIds);
      // Normalize array of resources
      const values = normalizeData(jsonApiResponse.data) || [];
      return { partitionTypeId, values };
    } catch (error) {
      let errorMessage = 'Failed to reorder partition values';
      if (error.response?.data?.errors?.[0]?.detail) {
        errorMessage = error.response.data.errors[0].detail;
      } else if (error.apiError?.message) {
        errorMessage = error.apiError.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// Slice
const partitionValuesSlice = createSlice({
  name: 'partitionValues',
  initialState: partitionValuesAdapter.getInitialState({
    loading: false,
    error: null,
    pagination: null,
    included: null,
  }),
  reducers: {
    retireValues: (state, action) => {
      partitionValuesAdapter.updateMany(state, action.payload.map((id) => ({ id, changes: { is_active: false } })));
    },
    /**
     * Upsert many partition values (for entity processor)
     */
    upsertMany: (state, action) => {
      partitionValuesAdapter.upsertMany(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchPartitionValuesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPartitionValuesThunk.fulfilled, (state, action) => {
        state.loading = false;
        const { values } = action.payload;
        partitionValuesAdapter.upsertMany(state, values);
        state.pagination = action.payload?.meta?.pagination || null;
        state.included = action.payload?.included || null;
      })
      .addCase(fetchPartitionValuesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create
      .addCase(createPartitionValueThunk.fulfilled, (state, action) => {
        const { value } = action.payload;
        partitionValuesAdapter.upsertOne(state, value);
      })
      // Update
      .addCase(updatePartitionValueThunk.fulfilled, (state, action) => {
        const { value } = action.payload;
        partitionValuesAdapter.upsertOne(state, value);
      })
      // Delete
      .addCase(deletePartitionValueThunk.fulfilled, (state, action) => {
        const { id } = action.payload;
        partitionValuesAdapter.removeOne(state, id);
      })
      // Bulk Create
      .addCase(bulkCreatePartitionValuesThunk.fulfilled, (state, action) => {
        const { values } = action.payload;
        partitionValuesAdapter.upsertMany(state, values);
      })
      // Reorder
      .addCase(reorderPartitionValuesThunk.fulfilled, (state, action) => {
        const { values } = action.payload;
        partitionValuesAdapter.upsertMany(state, values);
      });
  },
});

// Entity adapter selectors
export const {
  selectAll: selectAllPartitionValues,
  selectById: selectPartitionValueById,
  selectIds: selectPartitionValueIds,
} = partitionValuesAdapter.getSelectors((state) => state.partitionValues || partitionValuesAdapter.getInitialState());

// Memoized selector to get partition values by partition type ID
export const selectPartitionValues = createSelector(
  [selectAllPartitionValues, (_state, partitionTypeId) => partitionTypeId],
  (allValues, partitionTypeId) =>
    allValues.filter((v) => v.partition_type?.id === partitionTypeId)
);

export const selectPartitionValuesLoading = (state) => state.partitionValues?.loading || false;

// Export actions
export const { upsertMany, retireValues } = partitionValuesSlice.actions;

export default partitionValuesSlice.reducer;
